/** Sound categories, mirroring vanilla's Music & Sounds options (plus ui). */
export const CATEGORIES = ['master', 'music', 'blocks', 'hostile', 'friendly', 'players', 'ambient', 'weather', 'ui'] as const;
export type Category = typeof CATEGORIES[number];

/** One layer of a procedural placeholder sound (rendered offline with Web Audio). */
export type SynthLayer =
  | {
    k: 'noise'; t?: number; dur: number;
    /** Biquad filter applied to the noise (default none). */
    filt?: BiquadFilterType; f?: number; f1?: number; q?: number;
    /** Attack seconds (default 0.003); decay is exponential over the rest. */
    a?: number; g?: number;
    /** 'brown' = low-weighted noise (thumps, rumble). */
    color?: 'white' | 'brown';
    /** Amplitude modulation [rateHz, depth 0..1] (crackle, growl texture). */
    am?: [number, number];
    hold?: number;
    /** Granular: instead of continuous noise, this many micro-bursts (2-8 ms) scattered over dur. */
    clicks?: number;
  }
  | {
    k: 'osc'; t?: number; dur: number; wave: OscillatorType;
    /** Start / end frequency (exponential sweep when f1 is set). */
    f: number; f1?: number;
    a?: number; g?: number;
    /** Vibrato [rateHz, depthHz]. */
    vib?: [number, number];
    am?: [number, number];
    filt?: BiquadFilterType; ff?: number; q?: number;
    /** Hold at full level for this fraction of dur before decaying (pads, pops). */
    hold?: number;
    /** Parallel band-pass formants [Hz, Q] (voices: oof, moo, growl). */
    formants?: [number, number][];
  };

export interface SoundDef {
  id: string;
  category: Category;
  /** Base gain 0..1 (before category / master). */
  volume: number;
  /** Random playback-rate range (vanilla mostly 0.8..1.2). */
  pitch: [number, number];
  /** Random volume variance: gain *= 1 - rand * this. */
  volVar: number;
  /** Plays at a world position through a PannerNode. */
  positional: boolean;
  /** Linear attenuation distance in blocks (vanilla 16). */
  maxDist: number;
  /** Procedural placeholder: a list of variants, each a list of layers. */
  synth: SynthLayer[][];
  /** Reuse another id's rendered placeholder (vanilla: hit/fall reuse the step set, place reuses break). */
  shareWith?: string;
}
